import React, { useState, useMemo, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";

// ─────────────────────────────────────────────────────────────────────────────
// HENRY-GRIFFITTS CARD & SUBSCRIPTIONS — third module inside the CFO tab.
// The monthly review of the Chase Ink card: upload the statement PDF, the server
// reconciles it to the cent against Chase's own totals, categorises it, and builds
// the subscription register and the "To check" list (backend: cfo_card_api.py).
//   · every number here comes from /api/cfo/card — nothing is recomputed in the
//     browser except filtering, sorting and the CSV of what is on screen
//   · money arrives as INTEGER CENTS and is only formatted here
//   · a PDF is never kept: a dry run stores nothing, so Import sends the same files
// ─────────────────────────────────────────────────────────────────────────────

const API = "/api";
const NAVY = "#102134", GREEN = "#48B64C", GOLD = "#966E40";
const MUTE = "#6B7280", INK = "#1A1A1A", RED = "#C0392B";
// Stacked-bar colours, in fixed group order. Checked for colour-blind separation;
// the category table under the chart is the table view of the same numbers.
const GROUP_COLORS = ["#2a78d6", "#eb6834", "#1baf7a", "#eda100", "#4a3aa7", "#e87ba4"];
const MAX_FILES = 25, MAX_BYTES = 5 * 1024 * 1024;
const EMPTY_TEXT = "Each month, download the new statement PDF from Chase and upload it here. It is checked against Chase's totals before anything is saved.";

const money = (cents) => {
  if (cents == null) return "—";
  const a = Math.abs(Math.round(cents));
  return (cents < 0 ? "-$" : "$") + Math.floor(a / 100).toLocaleString("en-US") + "." + String(a % 100).padStart(2, "0");
};
const money0 = (cents) => (cents == null ? "—" : (cents < 0 ? "-$" : "$") + Math.round(Math.abs(cents) / 100).toLocaleString("en-US"));
const monthLabel = (ym) => new Date(+ym.slice(0, 4), +ym.slice(5, 7) - 1, 1).toLocaleString("en-US", { month: "short", year: "2-digit" });
const errText = (e, fallback) => e?.response?.data?.detail || fallback;
const rulePattern = (desc) => desc.split(" ").slice(0, 2).join(" ").replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

export default function CFOCard() {
  const [data, setData] = useState(null);
  const [txns, setTxns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [section, setSection] = useState("overview");
  const [notice, setNotice] = useState("");

  const load = useCallback(async () => {
    try {
      const [o, t] = await Promise.all([axios.get(`${API}/cfo/card`), axios.get(`${API}/cfo/card/txns`)]);
      setData(o.data);
      setTxns(t.data.txns || []);
      setLoadError("");
    } catch (e) {
      // A failed load is "could not read", never "there is nothing" — say so.
      setLoadError(errText(e, "Couldn't load the card data."));
    }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const say = (text) => { setNotice(text); setTimeout(() => setNotice((n) => (n === text ? "" : n)), 4000); };
  const act = async (promise, done) => {
    try { await promise; await load(); if (done) say(done); }
    catch (e) { say(errText(e, "That didn't save. Try again.")); }
  };

  const ov = data?.overview;
  const empty = !data || !ov || ov.empty;
  const reg = data?.register;

  return (
    <div style={{ maxWidth: 1000, margin: "0 auto", padding: "22px 20px 70px", fontFamily: "'Inter',system-ui,sans-serif", color: INK }}>
      <style>{`
        .cc-root *{box-sizing:border-box;}
        .cc-root .fr{font-family:'Fraunces',Georgia,serif;}
        .cc-card{background:#fff;border:1px solid #ECE7DE;border-radius:14px;}
        .cc-btn{cursor:pointer;border:none;border-radius:9px;font-weight:600;font-family:inherit;transition:transform .08s;}
        .cc-btn:active{transform:translateY(1px);}
        .cc-btn:disabled{opacity:.5;cursor:default;}
        .cc-root select,.cc-root input{font-family:inherit;font-size:12.5px;color:${INK};}
        .cc-root select{padding:4px 6px;border-radius:7px;border:1px solid #D9D3C8;background:#fff;}
        .cc-in{border:1px solid #E7E2D8;border-radius:6px;padding:5px 7px;background:#fff;}
        .cc-seg{padding:8px 14px;border-radius:9px;font-size:13px;font-weight:600;cursor:pointer;border:1px solid #D9D3C8;background:#fff;color:${NAVY};}
        .cc-seg.on{background:${NAVY};color:#fff;border-color:${NAVY};}
        .cc-root table{width:100%;border-collapse:collapse;font-size:12.5px;}
        .cc-root th{color:${MUTE};text-align:left;font-size:10.5px;text-transform:uppercase;letter-spacing:.5px;padding:5px 6px;font-weight:600;white-space:nowrap;}
        .cc-root td{padding:6px;border-top:1px solid #F0EBE2;vertical-align:top;}
        .cc-root .num{text-align:right;font-variant-numeric:tabular-nums;white-space:nowrap;}
        .cc-root th.sort{cursor:pointer;}
        .cc-chip{display:inline-block;padding:2px 8px;border-radius:999px;font-size:11px;font-weight:600;white-space:nowrap;}
      `}</style>

      <div className="cc-root">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: 10, marginBottom: 14 }}>
          <div>
            <div className="fr" style={{ fontSize: 24, fontWeight: 600, color: NAVY }}>Card & Subscriptions<span style={{ color: GREEN }}>.</span></div>
            <div style={{ color: MUTE, fontSize: 13, marginTop: 2 }}>The Chase Ink card, month by month — reconciled to Chase's own totals before anything is saved.</div>
          </div>
          {notice && <span className="cc-chip" style={{ background: "#F3EFE7", color: NAVY, padding: "4px 11px", fontSize: 12 }}>{notice}</span>}
        </div>

        {loading && <div className="cc-card" style={{ padding: 26, color: MUTE, fontSize: 14 }}>Loading the card data…</div>}
        {!loading && loadError && <div className="cc-card" style={{ padding: 26, color: RED, fontSize: 14 }}>{loadError}</div>}

        {!loading && !loadError && empty && (
          <>
            <div className="cc-card" style={{ padding: 30, textAlign: "center", marginBottom: 16 }}>
              <div className="fr" style={{ fontSize: 20, fontWeight: 600, color: NAVY }}>No statements yet</div>
              <div style={{ color: MUTE, fontSize: 14, margin: "8px auto 0", maxWidth: 540 }}>{EMPTY_TEXT}</div>
            </div>
            <Uploader onDone={load} />
          </>
        )}

        {!loading && !loadError && !empty && (
          <>
            <div className="cc-card" style={{ padding: "18px 22px", marginBottom: 16, display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 18 }}>
              <Tile label={`Spending, last ${ov.window.length} full months`} value={money0(ov.last12_cents)}
                sub={ov.window.length ? `${monthLabel(ov.window[0])} – ${monthLabel(ov.window[ov.window.length - 1])}` : ""} />
              <Tile label="Average per month" value={money0(ov.avg_month_cents)} />
              <Tile label="Interest and late fees" value={money(ov.interest_late_fees_cents)}
                color={ov.interest_late_fees_cents > 0 ? RED : GREEN} sub="all imported statements" />
              <Tile label="Cash back not redeemed" value={money(ov.cash_back_cents)} color={GOLD}
                sub={`${ov.points_total.toLocaleString("en-US")} points`} />
              <Tile label="Subscriptions per month" value={money(reg.total_now_cents)}
                sub={reg.total_year_ago_cents == null ? "no statement a year earlier" : `${money(reg.total_year_ago_cents)} a year ago`} />
              <Tile label="Open checks" value={String(data.open_checks)} color={data.open_checks ? RED : GREEN} />
            </div>

            <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
              {[["overview", "Spending"], ["subs", "Subscriptions"], ["checks", `To check (${data.open_checks})`],
                ["txns", "Transactions"], ["statements", "Statements"]].map(([k, lbl]) => (
                <div key={k} className={"cc-seg" + (section === k ? " on" : "")} onClick={() => setSection(k)}>{lbl}</div>
              ))}
            </div>

            {section === "overview" && <Spending ov={ov} groups={data.chart_groups} />}
            {section === "subs" && <Subscriptions data={data} act={act} />}
            {section === "checks" && <Checks checks={data.checks} txns={txns} act={act} />}
            {section === "txns" && <Transactions txns={txns} categories={data.categories} act={act} />}
            {section === "statements" && (<><Uploader onDone={load} /><Statements statements={data.statements} /></>)}

            <div style={{ marginTop: 18, fontSize: 11.5, color: MUTE, textAlign: "center" }} className="fr">
              Henry-Griffitts · We Deliver Confidence · internal working document
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Tile({ label, value, sub, color }) {
  return (
    <div>
      <div style={{ fontSize: 10.5, color: "#8A8276", textTransform: "uppercase", letterSpacing: 1, fontWeight: 600 }}>{label}</div>
      <div className="fr" style={{ fontSize: 24, fontWeight: 600, color: color || NAVY, lineHeight: 1.15, marginTop: 3 }}>{value}</div>
      {sub && <div style={{ fontSize: 11.5, color: MUTE, marginTop: 2 }}>{sub}</div>}
    </div>
  );
}
function Title({ children, right }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
      <div className="fr" style={{ fontSize: 18, fontWeight: 600, color: NAVY }}>{children}</div>
      {right}
    </div>
  );
}

// ---- Spending by month + Where the money goes --------------------------------
function Spending({ ov, groups }) {
  const rows = ov.months.map((m) => {
    const r = { month: monthLabel(m.month) + (m.full ? "" : "*") };
    groups.forEach((g) => { r[g] = (m.groups[g] || 0) / 100; });
    return r;
  });
  const partial = ov.months.some((m) => !m.full);
  const sum = (key) => ov.categories.reduce((s, c) => s + c[key], 0);
  return (
    <>
      <div className="cc-card" style={{ padding: "18px 22px", marginBottom: 16 }}>
        <Title>Spending by month</Title>
        <div style={{ width: "100%", height: 320 }}>
          <ResponsiveContainer>
            <BarChart data={rows} margin={{ top: 6, right: 6, left: 6, bottom: 0 }} barCategoryGap="22%">
              <CartesianGrid stroke="#F0EBE2" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: MUTE }} tickLine={false} axisLine={{ stroke: "#D9D3C8" }} interval="preserveStartEnd" />
              <YAxis tick={{ fontSize: 11, fill: MUTE }} tickLine={false} axisLine={false} width={52}
                tickFormatter={(v) => "$" + (v >= 1000 ? (v / 1000).toFixed(0) + "k" : v)} />
              <Tooltip cursor={{ fill: "#F7F4EE" }} formatter={(v, name) => [money(Math.round(v * 100)), name]}
                contentStyle={{ fontSize: 12, borderRadius: 9, border: "1px solid #ECE7DE", color: INK }} itemStyle={{ color: INK }} />
              <Legend iconType="square" iconSize={10} wrapperStyle={{ fontSize: 12, color: INK }} formatter={(v) => <span style={{ color: INK }}>{v}</span>} />
              {groups.map((g, i) => (
                <Bar key={g} dataKey={g} stackId="s" fill={GROUP_COLORS[i]} stroke="#fff" strokeWidth={1} isAnimationActive={false}
                  radius={i === groups.length - 1 ? [3, 3, 0, 0] : 0} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div style={{ fontSize: 11.5, color: MUTE, marginTop: 6 }}>
          By transaction date, net of credits. Card payments are not spending.{partial ? " * A month the statements only partly cover." : ""}
        </div>
      </div>

      <div className="cc-card" style={{ padding: "18px 22px", marginBottom: 16 }}>
        <Title>Where the money goes</Title>
        <div style={{ overflowX: "auto" }}>
          <table style={{ minWidth: 640 }}>
            <thead><tr>
              <th>Category</th><th className="num">Last {ov.window.length} months</th><th className="num">Share</th>
              <th className="num">This year to date</th><th className="num">Same months last year</th><th className="num">All time</th>
            </tr></thead>
            <tbody>
              {ov.categories.map((c) => (
                <tr key={c.category}>
                  <td><span style={{ display: "inline-block", width: 9, height: 9, borderRadius: 2, marginRight: 7, background: GROUP_COLORS[groups.indexOf(c.group)] }} />{c.category}</td>
                  <td className="num">{money(c.last12_cents)}</td>
                  <td className="num">{(c.share * 100).toFixed(1)}%</td>
                  <td className="num">{money(c.ytd_cents)}</td>
                  <td className="num" style={{ color: MUTE }}>{money(c.ytd_last_year_cents)}</td>
                  <td className="num">{money(c.all_cents)}</td>
                </tr>
              ))}
              <tr style={{ fontWeight: 700 }}>
                <td>Total</td><td className="num">{money(sum("last12_cents"))}</td><td className="num">100.0%</td>
                <td className="num">{money(sum("ytd_cents"))}</td><td className="num">{money(sum("ytd_last_year_cents"))}</td><td className="num">{money(sum("all_cents"))}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div style={{ fontSize: 11.5, color: MUTE, marginTop: 8 }}>Year to date is {ov.ytd_label}; last year is the {ov.ytd_last_year_label}.</div>
      </div>
    </>
  );
}

// ---- Subscriptions: the register ---------------------------------------------
const REC_COLOR = { Keep: GREEN, Cancel: RED, "Cancel with old site": RED, Stopped: MUTE, "Find out": GOLD, "Right-size": "#2a78d6", "Pause between projects": "#4a3aa7" };

function Subscriptions({ data, act }) {
  const reg = data.register;
  const [rec, setRec] = useState("");
  const [dec, setDec] = useState("");
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({ vendor: "", cadence: "Monthly", what: "", recommendation: "Find out", note: "" });
  const recs = useMemo(() => [...new Set(reg.rows.map((r) => r.recommendation).filter(Boolean))], [reg.rows]);
  const rows = reg.rows.filter((r) => (!rec || r.recommendation === rec) && (!dec || r.decision === dec));
  const save = (id, body, done) => act(axios.put(`${API}/cfo/card/vendors/${id}`, body), done);
  const dismiss = (vendor, dismissed) => act(axios.put(`${API}/cfo/card/dismissed`, { vendor, dismissed }), dismissed ? "Marked not a subscription" : "Undone");

  return (
    <>
      {reg.flags.length > 0 && (
        <div className="cc-card" style={{ padding: "14px 18px", marginBottom: 14, borderLeft: `4px solid ${GOLD}` }}>
          <div style={{ fontSize: 11, color: GOLD, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, marginBottom: 6 }}>Flagged</div>
          {reg.flags.map((f, i) => (
            <div key={i} style={{ fontSize: 13, padding: "3px 0", display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
              <span>{f.text}</span>
              {f.kind === "new" && (
                <button className="cc-btn" onClick={() => dismiss(f.vendor, true)} style={{ background: "#F3EFE7", color: NAVY, padding: "3px 10px", fontSize: 11.5 }}>Not a subscription</button>
              )}
            </div>
          ))}
        </div>
      )}
      {(reg.dismissed || []).length > 0 && (
        <div style={{ fontSize: 12, color: MUTE, margin: "0 4px 14px" }}>
          Marked not a subscription:{" "}
          {reg.dismissed.map((d) => (
            <span key={d.vendor} style={{ marginRight: 12, whiteSpace: "nowrap" }} title={`${(d.dismissed_by || "").split("@")[0]} · ${(d.dismissed_at || "").slice(0, 10)}`}>
              {d.vendor}{" "}
              <button className="cc-btn" onClick={() => dismiss(d.vendor, false)} style={{ background: "transparent", color: GOLD, padding: 0, fontSize: 12, textDecoration: "underline" }}>Undo</button>
            </span>
          ))}
        </div>
      )}

      <div className="cc-card" style={{ padding: "18px 22px", marginBottom: 16 }}>
        <Title right={
          <span style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <select value={rec} onChange={(e) => setRec(e.target.value)}><option value="">All recommendations</option>{recs.map((r) => <option key={r}>{r}</option>)}</select>
            <select value={dec} onChange={(e) => setDec(e.target.value)}><option value="">All decisions</option>{data.decisions.map((d) => <option key={d}>{d}</option>)}</select>
            <button className="cc-btn" onClick={() => setAdding(!adding)} style={{ background: GREEN, color: "#fff", padding: "6px 14px", fontSize: 12.5 }}>+ Add vendor</button>
          </span>}>
          Subscriptions — {money(reg.total_now_cents)} a month
        </Title>
        <div style={{ fontSize: 12, color: MUTE, marginBottom: 10 }}>
          As of the {reg.as_of} statement{reg.year_ago_as_of ? `; a year earlier is the ${reg.year_ago_as_of} statement (${money(reg.total_year_ago_cents)} a month)` : "; no statement from a year earlier is imported"}. Yearly charges are spread over 12 months.
        </div>

        {adding && (
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", padding: "10px 12px", background: "#F7F4EE", borderRadius: 10, marginBottom: 12 }}>
            <input className="cc-in" placeholder="Vendor, named exactly as its rule names it" value={draft.vendor} onChange={(e) => setDraft({ ...draft, vendor: e.target.value })} style={{ width: 250 }} />
            <select value={draft.cadence} onChange={(e) => setDraft({ ...draft, cadence: e.target.value })}>{data.cadences.map((c) => <option key={c}>{c}</option>)}</select>
            <input className="cc-in" placeholder="What it is" value={draft.what} onChange={(e) => setDraft({ ...draft, what: e.target.value })} style={{ width: 200 }} />
            <input className="cc-in" placeholder="Recommendation" value={draft.recommendation} onChange={(e) => setDraft({ ...draft, recommendation: e.target.value })} style={{ width: 130 }} />
            <button className="cc-btn" disabled={!draft.vendor.trim()} style={{ background: NAVY, color: "#fff", padding: "6px 14px", fontSize: 12.5 }}
              onClick={() => act(axios.post(`${API}/cfo/card/vendors`, draft).then(() => { setAdding(false); setDraft({ ...draft, vendor: "", what: "", note: "" }); }), "Vendor added")}>Add</button>
          </div>
        )}

        <div style={{ overflowX: "auto" }}>
          <table style={{ minWidth: 900 }}>
            <thead><tr>
              <th>Vendor</th><th>Recommendation</th><th>Decision</th><th className="num">Per month now</th>
              <th className="num">A year earlier</th><th className="num">Paid</th><th>Last charge</th><th style={{ minWidth: 220 }}>Note</th>
            </tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td><b>{r.vendor}</b><div style={{ color: MUTE, fontSize: 11.5 }}>{r.what}{r.cadence === "Yearly" ? " · yearly" : ""}</div></td>
                  <td><span className="cc-chip" style={{ background: (REC_COLOR[r.recommendation] || MUTE) + "1F", color: INK, border: `1px solid ${REC_COLOR[r.recommendation] || MUTE}` }}>{r.recommendation || "—"}</span></td>
                  <td>
                    <select value={r.decision || "Undecided"} onChange={(e) => save(r.id, { decision: e.target.value }, "Decision saved")}>
                      {data.decisions.map((d) => <option key={d}>{d}</option>)}
                    </select>
                    {r.decision_by && <div style={{ color: MUTE, fontSize: 10.5, marginTop: 2 }}>{r.decision_by.split("@")[0]} · {(r.decision_at || "").slice(0, 10)}</div>}
                  </td>
                  <td className="num"><b>{money(r.now_cents)}</b></td>
                  <td className="num" style={{ color: MUTE }}>{money(r.year_ago_cents)}</td>
                  <td className="num">{money(r.paid_cents)}</td>
                  <td style={{ whiteSpace: "nowrap" }}>{r.last_charge || "—"}</td>
                  <td>
                    {r.status_note && <div style={{ color: RED, fontSize: 12, marginBottom: 3 }}>{r.status_note}</div>}
                    <NoteInput value={r.note} onSave={(v) => save(r.id, { note: v }, "Note saved")} />
                  </td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={8} style={{ color: MUTE }}>No vendors match these filters.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <div className="cc-card" style={{ padding: "18px 22px", marginBottom: 16 }}>
        <Title>Usage — not part of the subscription total</Title>
        <div style={{ overflowX: "auto" }}>
          <table style={{ minWidth: 760 }}>
            <thead><tr><th>Vendor</th><th>Recommendation</th><th>Decision</th><th className="num">Average, last 3 months</th><th className="num">Paid</th><th>Last charge</th><th style={{ minWidth: 220 }}>Note</th></tr></thead>
            <tbody>
              {reg.usage.map((r) => (
                <tr key={r.id}>
                  <td><b>{r.vendor}</b><div style={{ color: MUTE, fontSize: 11.5 }}>{r.what}</div></td>
                  <td>{r.recommendation}</td>
                  <td><select value={r.decision || "Undecided"} onChange={(e) => save(r.id, { decision: e.target.value }, "Decision saved")}>{data.decisions.map((d) => <option key={d}>{d}</option>)}</select></td>
                  <td className="num"><b>{money(r.avg3_cents)}</b></td>
                  <td className="num">{money(r.paid_cents)}</td>
                  <td style={{ whiteSpace: "nowrap" }}>{r.last_charge || "—"}</td>
                  <td><NoteInput value={r.note} onSave={(v) => save(r.id, { note: v }, "Note saved")} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="cc-card" style={{ padding: "18px 22px", marginBottom: 16 }}>
        <Title>Postage accounts</Title>
        <table>
          <thead><tr><th>Account</th><th className="num">Refills</th><th className="num">Total refilled</th><th>Last refill</th></tr></thead>
          <tbody>
            {data.postage.map((p) => (
              <tr key={p.account}><td>{p.account}</td><td className="num">{p.refills}</td><td className="num">{money(p.total_cents)}</td>
                <td>{p.last_date ? `${p.last_date} · ${money(p.last_cents)}` : "none on the imported statements"}</td></tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function NoteInput({ value, onSave, placeholder }) {
  const [v, setV] = useState(value || "");
  useEffect(() => { setV(value || ""); }, [value]);
  return (
    <input className="cc-in" value={v} placeholder={placeholder || "Note"} onChange={(e) => setV(e.target.value)}
      onBlur={() => { if (v.trim() !== (value || "").trim()) onSave(v.trim()); }}
      onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }} style={{ width: "100%" }} />
  );
}

// ---- To check ------------------------------------------------------------------
const KIND_LABEL = { cashback: "Cash back", duplicate: "Possible duplicate", large: "Large charge", note: "Needs a business note" };

function Checks({ checks, act }) {
  const [showDone, setShowDone] = useState(false);
  const [kind, setKind] = useState("");
  const open = checks.filter((c) => !c.reviewed);
  const list = (showDone ? checks : open).filter((c) => !kind || c.kind === kind);
  const put = (c, body, done) => act(axios.put(`${API}/cfo/card/checks/${c.id}`, body), done);
  return (
    <div className="cc-card" style={{ padding: "18px 22px", marginBottom: 16 }}>
      <Title right={
        <span style={{ display: "flex", gap: 10, alignItems: "center", fontSize: 12.5 }}>
          <select value={kind} onChange={(e) => setKind(e.target.value)}><option value="">All kinds</option>{Object.entries(KIND_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
          <label style={{ cursor: "pointer" }}><input type="checkbox" checked={showDone} onChange={(e) => setShowDone(e.target.checked)} /> show reviewed</label>
        </span>}>
        To check — {open.length} open
      </Title>
      {list.length === 0 && <div style={{ color: MUTE, fontSize: 14 }}>Nothing open. Every check has been reviewed.</div>}
      {list.map((c) => (
        <div key={c.id} style={{ display: "flex", gap: 12, alignItems: "flex-start", padding: "10px 0", borderTop: "1px solid #F0EBE2", opacity: c.reviewed ? 0.6 : 1, flexWrap: "wrap" }}>
          <div style={{ flex: "1 1 320px", minWidth: 0 }}>
            <div style={{ fontSize: 13.5, fontWeight: 600 }}>{c.title}</div>
            <div style={{ fontSize: 12, color: MUTE, marginTop: 2 }}>{c.detail}</div>
            {c.reviewed && <div style={{ fontSize: 11, color: MUTE, marginTop: 2 }}>Reviewed by {(c.reviewed_by || "").split("@")[0]} · {(c.reviewed_at || "").slice(0, 10)}</div>}
          </div>
          <div style={{ flex: "1 1 240px" }}>
            {c.kind === "note"
              ? <NoteInput value="" placeholder="Business note — what was this for?" onSave={(v) => v && act(axios.put(`${API}/cfo/card/txns/${c.txn_ids[0]}`, { business_note: v }), "Note saved")} />
              : <NoteInput value={c.note} onSave={(v) => put(c, { note: v }, "Note saved")} />}
          </div>
          <button className="cc-btn" onClick={() => put(c, { reviewed: !c.reviewed }, c.reviewed ? "Reopened" : "Marked reviewed")}
            style={{ background: c.reviewed ? "#F3EFE7" : NAVY, color: c.reviewed ? NAVY : "#fff", padding: "6px 12px", fontSize: 12 }}>
            {c.reviewed ? "Reopen" : "Mark reviewed"}
          </button>
        </div>
      ))}
    </div>
  );
}

// ---- Transactions ----------------------------------------------------------------
function csvCell(v) { const s = String(v ?? ""); return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s; }

function Transactions({ txns, categories, act }) {
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [card, setCard] = useState("");
  const [sort, setSort] = useState({ key: "date", dir: -1 });
  const [limit, setLimit] = useState(200);
  const [edit, setEdit] = useState(null);   // {id, category, vendor, subcategory, pattern}
  const cards = useMemo(() => [...new Set(txns.map((t) => t.card))].sort(), [txns]);
  const cats = useMemo(() => [...new Set(txns.map((t) => t.category))].sort(), [txns]);

  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const out = txns.filter((t) => (!cat || t.category === cat) && (!card || t.card === card)
      && (!needle || `${t.description} ${t.vendor} ${t.business_note || ""}`.toLowerCase().includes(needle)));
    const k = sort.key;
    out.sort((a, b) => (a[k] < b[k] ? -1 : a[k] > b[k] ? 1 : 0) * sort.dir);
    return out;
  }, [txns, q, cat, card, sort]);
  const total = rows.reduce((s, t) => s + (t.category === "Payment" ? 0 : t.amount_cents), 0);

  function exportCsv() {
    const head = ["Date", "Statement", "Card", "Cardholder", "Description", "Vendor", "Category", "Subcategory", "Amount", "Business note"];
    const lines = [head.join(",")].concat(rows.map((t) => [t.date, t.statement_close, t.card, t.cardholder, t.description, t.vendor,
      t.category, t.subcategory, (t.amount_cents / 100).toFixed(2), t.business_note].map(csvCell).join(",")));
    const url = URL.createObjectURL(new Blob([lines.join("\r\n")], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url; a.download = "hg-card-transactions.csv"; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
  function saveEdit(asRule) {
    const body = { category: edit.category, vendor: edit.vendor, subcategory: edit.subcategory };
    if (asRule) body.rule_pattern = edit.pattern;
    act(axios.put(`${API}/cfo/card/txns/${edit.id}`, body).then((r) => { setEdit(null); return r; }), asRule ? "Saved, and a rule added" : "Category saved");
  }
  const Th = ({ k, children, num }) => (
    <th className={"sort" + (num ? " num" : "")} onClick={() => setSort({ key: k, dir: sort.key === k ? -sort.dir : 1 })}>
      {children}{sort.key === k ? (sort.dir > 0 ? " ▲" : " ▼") : ""}
    </th>
  );

  return (
    <div className="cc-card" style={{ padding: "18px 22px", marginBottom: 16 }}>
      <Title right={
        <span style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <input className="cc-in" placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)} style={{ width: 170 }} />
          <select value={cat} onChange={(e) => setCat(e.target.value)}><option value="">All categories</option>{cats.map((c) => <option key={c}>{c}</option>)}</select>
          <select value={card} onChange={(e) => setCard(e.target.value)}><option value="">Both cards</option>{cards.map((c) => <option key={c} value={c}>Card {c}</option>)}</select>
          <button className="cc-btn" onClick={exportCsv} style={{ background: "#fff", color: NAVY, border: "1px solid #D9D3C8", padding: "6px 12px", fontSize: 12.5 }}>Export CSV</button>
        </span>}>
        Transactions
      </Title>
      <div style={{ fontSize: 12, color: MUTE, marginBottom: 8 }}>{rows.length.toLocaleString("en-US")} rows · {money(total)} of spending (payments left out)</div>
      <div style={{ overflowX: "auto" }}>
        <table style={{ minWidth: 900 }}>
          <thead><tr><Th k="date">Date</Th><Th k="card">Card</Th><Th k="description">Description</Th><Th k="vendor">Vendor</Th><Th k="category">Category</Th><Th k="amount_cents" num>Amount</Th><th style={{ minWidth: 180 }}>Business note</th></tr></thead>
          <tbody>
            {rows.slice(0, limit).map((t) => (
              <React.Fragment key={t.id}>
                <tr>
                  <td style={{ whiteSpace: "nowrap" }}>{t.date}</td>
                  <td>{t.card}</td>
                  <td style={{ color: MUTE, fontSize: 12 }}>{t.description}</td>
                  <td>{t.vendor}{t.rule_id === "manual" && <span style={{ color: GOLD, fontSize: 10.5 }}> · set by hand</span>}</td>
                  <td>
                    {t.category === "Payment" ? <span style={{ color: MUTE }}>Payment</span> : (
                      <select value={categories.includes(t.category) ? t.category : ""} style={t.category === "Uncategorized" ? { borderColor: RED } : undefined}
                        onChange={(e) => setEdit({ id: t.id, category: e.target.value, vendor: t.category === "Uncategorized" ? "" : t.vendor, subcategory: "", pattern: rulePattern(t.description) })}>
                        {!categories.includes(t.category) && <option value="">Uncategorized</option>}
                        {categories.map((c) => <option key={c}>{c}</option>)}
                      </select>
                    )}
                  </td>
                  <td className="num" style={{ color: t.amount_cents < 0 ? GREEN : INK }}>{money(t.amount_cents)}</td>
                  <td>{t.category !== "Payment" && <NoteInput value={t.business_note} placeholder="" onSave={(v) => act(axios.put(`${API}/cfo/card/txns/${t.id}`, { business_note: v }), "Note saved")} />}</td>
                </tr>
                {edit && edit.id === t.id && (
                  <tr><td colSpan={7} style={{ background: "#F7F4EE" }}>
                    <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                      <b style={{ fontSize: 12.5 }}>→ {edit.category}</b>
                      <input className="cc-in" placeholder="Vendor name" value={edit.vendor} onChange={(e) => setEdit({ ...edit, vendor: e.target.value })} style={{ width: 180 }} />
                      <input className="cc-in" placeholder="Subcategory (optional)" value={edit.subcategory} onChange={(e) => setEdit({ ...edit, subcategory: e.target.value })} style={{ width: 160 }} />
                      <button className="cc-btn" disabled={!edit.vendor.trim()} onClick={() => saveEdit(false)} style={{ background: NAVY, color: "#fff", padding: "6px 12px", fontSize: 12 }}>Save this one</button>
                      <span style={{ fontSize: 12, color: MUTE }}>or, for every description containing</span>
                      <input className="cc-in" value={edit.pattern} onChange={(e) => setEdit({ ...edit, pattern: e.target.value })} style={{ width: 170 }} />
                      <button className="cc-btn" disabled={!edit.vendor.trim() || !edit.pattern.trim()} onClick={() => saveEdit(true)} style={{ background: GREEN, color: "#fff", padding: "6px 12px", fontSize: 12 }}>Save as a rule for this vendor</button>
                      <button className="cc-btn" onClick={() => setEdit(null)} style={{ background: "transparent", color: MUTE, padding: "6px 8px", fontSize: 12 }}>Cancel</button>
                    </div>
                  </td></tr>
                )}
              </React.Fragment>
            ))}
            {rows.length === 0 && <tr><td colSpan={7} style={{ color: MUTE }}>No transactions match.</td></tr>}
          </tbody>
        </table>
      </div>
      {rows.length > limit && (
        <button className="cc-btn" onClick={() => setLimit(rows.length)} style={{ background: "#F3EFE7", color: NAVY, padding: "7px 14px", fontSize: 12.5, marginTop: 10 }}>
          Show all {rows.length.toLocaleString("en-US")} rows
        </button>
      )}
    </div>
  );
}

// ---- Statements + upload ---------------------------------------------------------
const STATUS = {
  ready: { text: "Ready", color: GREEN }, imported: { text: "Imported", color: GREEN },
  duplicate: { text: "Already imported", color: MUTE }, refused: { text: "Refused", color: RED },
  unreadable: { text: "Could not read", color: RED },
};
const checkMark = (ok) => (ok === true ? "✓" : ok === false ? "✗" : "–");

function Uploader({ onDone }) {
  const [files, setFiles] = useState([]);
  const [results, setResults] = useState(null);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [imported, setImported] = useState(false);
  const ref = useRef();

  async function send(path, list) {
    const form = new FormData();
    list.forEach((f) => form.append("files", f));
    return (await axios.post(`${API}/cfo/card/${path}`, form)).data;
  }
  async function pick(e) {
    const list = Array.from(e.target.files || []);
    e.target.value = "";
    setResults(null); setImported(false); setError("");
    if (!list.length) return;
    if (list.length > MAX_FILES) { setError(`At most ${MAX_FILES} files at once.`); return; }
    const big = list.find((f) => f.size > MAX_BYTES);
    if (big) { setError(`${big.name} is over 5 MB.`); return; }
    setFiles(list); setBusy("Checking against Chase's totals…");
    try { setResults((await send("dry-run", list)).results); }
    catch (err) { setError(errText(err, "The check could not be run. Nothing was saved.")); }
    setBusy("");
  }
  async function doImport() {
    setBusy("Importing…"); setError("");
    try { setResults((await send("import", files)).results); setImported(true); setFiles([]); await onDone(); }
    catch (err) { setError(errText(err, "The import failed. Check the Statements list before trying again.")); }
    setBusy("");
  }
  const ready = (results || []).filter((r) => r.status === "ready").length;

  return (
    <div className="cc-card" style={{ padding: "18px 22px", marginBottom: 16 }}>
      <Title right={
        <span style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <input ref={ref} type="file" accept="application/pdf,.pdf" multiple onChange={pick} style={{ display: "none" }} />
          <button className="cc-btn" disabled={!!busy} onClick={() => ref.current.click()} style={{ background: GREEN, color: "#fff", padding: "8px 16px", fontSize: 13 }}>Upload statement PDFs</button>
          {!imported && ready > 0 && <button className="cc-btn" disabled={!!busy} onClick={doImport} style={{ background: NAVY, color: "#fff", padding: "8px 16px", fontSize: 13 }}>Import {ready} statement{ready === 1 ? "" : "s"}</button>}
        </span>}>
        Upload
      </Title>
      <div style={{ fontSize: 12.5, color: MUTE }}>{EMPTY_TEXT} Up to {MAX_FILES} files, 5 MB each. The PDF itself is never stored.</div>
      {busy && <div style={{ fontSize: 13, color: NAVY, marginTop: 10 }}>{busy}</div>}
      {error && <div style={{ fontSize: 13, color: RED, marginTop: 10 }}>{error}</div>}
      {results && (
        <div style={{ overflowX: "auto", marginTop: 12 }}>
          <div style={{ fontSize: 12, color: MUTE, marginBottom: 6 }}>{imported ? "Import result." : "Dry run — nothing has been saved yet."}</div>
          <table style={{ minWidth: 760 }}>
            <thead><tr><th>File</th><th>Closing</th><th>Result</th><th className="num">Transactions</th><th className="num">Purchases</th><th className="num">New balance</th><th>Checks a–f</th></tr></thead>
            <tbody>
              {results.map((r, i) => (
                <React.Fragment key={i}>
                  <tr>
                    <td style={{ fontSize: 12 }}>{r.file_name}</td>
                    <td>{r.closing_date || "—"}</td>
                    <td><b style={{ color: (STATUS[r.status] || {}).color }}>{(STATUS[r.status] || { text: r.status }).text}</b></td>
                    <td className="num">{r.txn_count ?? "—"}</td>
                    <td className="num">{r.totals ? money(r.totals.purchases) : "—"}</td>
                    <td className="num">{r.totals ? money(r.totals.new_balance) : "—"}</td>
                    <td style={{ letterSpacing: 3, fontWeight: 700 }}>{(r.checks || []).map((c) => <span key={c.id} title={`(${c.id}) ${c.label}`} style={{ color: c.ok === false ? RED : c.ok ? GREEN : MUTE }}>{checkMark(c.ok)}</span>)}</td>
                  </tr>
                  {(r.message && r.status !== "ready") && <tr><td colSpan={7} style={{ borderTop: "none", paddingTop: 0, fontSize: 12, color: r.status === "imported" ? MUTE : RED }}>{r.message}</td></tr>}
                  {(r.uncategorized || []).map((u, j) => (
                    <tr key={j}><td colSpan={7} style={{ borderTop: "none", paddingTop: 0, fontSize: 12, color: GOLD }}>Uncategorized: {u.date} · {u.description} · {money(u.amount_cents)} — give it a category in Transactions after importing.</td></tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Statements({ statements }) {
  return (
    <div className="cc-card" style={{ padding: "18px 22px", marginBottom: 16 }}>
      <Title>Imported statements</Title>
      <div style={{ overflowX: "auto" }}>
        <table style={{ minWidth: 860 }}>
          <thead><tr><th>Closing</th><th className="num">Purchases</th><th className="num">Fees</th><th className="num">Interest</th><th className="num">Payments + credits</th><th className="num">New balance</th><th className="num">Points</th><th className="num">Lines</th><th>Checks a–f</th><th>File</th></tr></thead>
          <tbody>
            {statements.map((s) => (
              <tr key={s.closing_date}>
                <td style={{ whiteSpace: "nowrap" }}><b>{s.closing_date}</b></td>
                <td className="num">{money(s.purchases)}</td><td className="num">{money(s.fees)}</td><td className="num">{money(s.interest)}</td>
                <td className="num">{money(s.payments_credits)}</td><td className="num">{money(s.new_balance)}</td>
                <td className="num">{(s.points_total || 0).toLocaleString("en-US")}</td><td className="num">{s.txn_count}</td>
                <td style={{ letterSpacing: 3, fontWeight: 700 }}>{(s.checks || []).map((c) => <span key={c.id} title={`(${c.id}) ${c.label}`} style={{ color: c.ok ? GREEN : MUTE }}>{checkMark(c.ok)}</span>)}</td>
                <td style={{ fontSize: 11.5, color: MUTE }} title={`SHA-256 ${s.sha256}`}>{s.file_name}<div>{(s.imported_at || "").slice(0, 10)} · {(s.imported_by || "").split("@")[0]}</div></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ fontSize: 11.5, color: MUTE, marginTop: 8 }}>A dash under (f) means the statement before it was not imported at the time, so its opening balance could not be compared.</div>
    </div>
  );
}
